<template>
    <div class="clarli-container">
        <!-- Header -->
        <div class="header">
            <h1>Template Verwaltung</h1>
            <button @click="showUploadModal = true" class="btn-primary">
                <i class="icon-plus"></i>
                Neues Template hochladen
            </button>
        </div>

        <!-- Template List -->
        <div class="template-list">
            <div v-if="loadingTemplates" class="loading-state">
                <div class="spinner"></div>
                <p>Templates werden geladen...</p>
            </div>
            <div v-else-if="templates.length === 0" class="empty-state">
                <p>Keine Templates vorhanden</p>
            </div>
            <div v-else class="template-table">
                <table>
                    <thead>
                        <tr>
                            <th>Template Name</th>
                            <th>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="template in templates" :key="template">
                            <td>{{ template }}</td>
                            <td>
                                <button @click="editTemplate(template)" class="btn-secondary">
                                    Regionen bearbeiten
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Upload Modal -->
        <div v-if="showUploadModal" class="modal-overlay" @click="showUploadModal = false">
            <div class="modal" @click.stop>
                <div class="modal-header">
                    <h2>Neues Template hochladen</h2>
                    <button @click="showUploadModal = false" class="btn-close">×</button>
                </div>
                <div class="modal-body">
                    <div class="file-upload">
                        <input
                               type="file"
                               ref="fileInput"
                               @change="handleFileSelect"
                               accept=".pdf"
                               style="display: none" />
                        <div
                             class="upload-area"
                             @click="$refs.fileInput.click()"
                             @dragover.prevent
                             @drop.prevent="handleFileDrop">
                            <div v-if="!selectedFile" class="upload-placeholder">
                                <i class="icon-upload"></i>
                                <p>PDF-Datei hier hineinziehen oder klicken zum Auswählen</p>
                            </div>
                            <div v-else class="file-selected">
                                <p>{{ selectedFile.name }}</p>
                                <button @click="selectedFile = null" class="btn-remove">Entfernen</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button @click="showUploadModal = false" class="btn-secondary">Abbrechen</button>
                    <button @click="uploadTemplate" :disabled="!selectedFile || uploading" class="btn-primary">
                        {{ uploading ? 'Wird hochgeladen...' : 'Hochladen' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Edit Template Modal -->
        <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
            <div class="modal large" @click.stop>
                <div class="modal-header">
                    <h2>Template bearbeiten: {{ selectedTemplateName }}</h2>
                    <button @click="closeEditModal" class="btn-close">×</button>
                </div>
                <div class="modal-body">
                    <div class="template-editor">
                        <div class="template-visualization">
                            <div v-if="loadingImage" class="image-loading-state">
                                <div class="spinner"></div>
                                <p>Template wird geladen...</p>
                            </div>
                            <div v-else class="canvas-container">
                                <div class="zoom-controls">
                                    <button @click="zoomOut" class="btn-zoom" :disabled="zoomLevel <= 0.25" title="Verkleinern">
                                        <i class="icon-zoom-out"></i>
                                    </button>
                                    <div class="zoom-level-display">
                                        <span class="zoom-percentage">{{ Math.round(zoomLevel * 100) }}%</span>
                                    </div>
                                    <button @click="zoomIn" class="btn-zoom" :disabled="zoomLevel >= 4" title="Vergrößern">
                                        <i class="icon-zoom-in"></i>
                                    </button>
                                    <button @click="resetZoom" class="btn-zoom-reset" title="Zoom zurücksetzen">
                                        Zurücksetzen
                                    </button>
                                </div>
                                <canvas
                                        ref="mainCanvas"
                                        class="main-canvas"
                                        @mousedown="startDrawing"
                                        @mousemove="draw"
                                        @mouseup="stopDrawing"
                                        @mouseleave="stopDrawing"
                                        @wheel="handleWheel"
                                        @contextmenu.prevent>
                                </canvas>
                            </div>
                        </div>
                        <div class="regions-panel">
                            <div class="regions-header">
                                <h3>Regionen</h3>
                                <div class="header-actions">
                                    <button @click="startNewRegion" class="btn-primary btn-sm">
                                        <i class="icon-plus"></i>
                                        neue Region hinzufügen
                                    </button>
                                </div>
                            </div>
                            <div v-if="loadingRegions" class="loading-state">
                                <div class="spinner"></div>
                                <p>Regionen werden geladen...</p>
                            </div>
                            <div v-else class="regions-content">
                                <div class="regions-list">
                                    <div
                                         v-for="(region, index) in regions"
                                         :key="index"
                                         class="region-item"
                                         :class="{ active: selectedRegionIndex === index }"
                                         @click="selectRegion(index)">
                                        <div class="region-info">
                                            <span class="region-name">{{ region.name }}</span>
                                            <span class="region-type">{{ region.type }}</span>
                                        </div>
                                        <div class="region-actions">
                                            <button @click.stop="deleteRegion(index)" class="btn-icon btn-delete" title="Löschen">
                                                <i class="icon-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Region Configuration Form -->
                                <div v-if="showRegionForm" class="region-form-panel">
                                    <div class="form-header">
                                        <h4>{{ isEditingRegion ? 'Region bearbeiten' : 'Neue Region hinzufügen' }}</h4>
                                        <button @click="closeRegionForm" class="btn-close-small">×</button>
                                    </div>

                                    <div class="region-form">
                                        <div class="form-group">
                                            <label for="regionName">Name:</label>
                                            <input
                                                   id="regionName"
                                                   v-model="currentRegion.name"
                                                   type="text"
                                                   class="form-control"
                                                   placeholder="Regionsname eingeben"
                                                   required>
                                        </div>

                                        <div class="form-group">
                                            <label for="regionType">Typ:</label>
                                            <select id="regionType" v-model="currentRegion.type" class="form-control">
                                                <option value="text">Text</option>
                                                <option value="checkbox">Checkbox</option>
                                                <option value="table">Table</option>
                                            </select>
                                        </div>

                                        <div v-if="currentRegion.type === 'text' || currentRegion.type === 'table'" class="form-group">
                                            <label>Inhaltseigenschaften einbeziehen:</label>
                                            <div class="checkbox-group">
                                                <label class="checkbox-label">
                                                    <input
                                                           type="checkbox"
                                                           v-model="currentRegion.includeTemplateTextAbove">
                                                    Text oberhalb einbeziehen
                                                </label>
                                                <label class="checkbox-label">
                                                    <input
                                                           type="checkbox"
                                                           v-model="currentRegion.includeTemplateTextBelow">
                                                    Text unterhalb einbeziehen
                                                </label>
                                                <label class="checkbox-label">
                                                    <input
                                                           type="checkbox"
                                                           v-model="currentRegion.includeTemplateTextLeft">
                                                    Text links einbeziehen
                                                </label>
                                                <label class="checkbox-label">
                                                    <input
                                                           type="checkbox"
                                                           v-model="currentRegion.includeTemplateTextRight">
                                                    Text rechts einbeziehen
                                                </label>
                                            </div>
                                        </div>

                                        <div v-if="currentRegion.type === 'checkbox'" class="form-group">
                                            <label for="text">Extrahierter Text:</label>
                                            <div v-if="currentRegion.text" class="text-display">
                                                {{ currentRegion.text }}
                                            </div>
                                            <div v-else class="text-hint">
                                                <i class="icon-info"></i>
                                                <span>Dieser Text wird nach dem Speichern vom Backend aktualisiert.</span>
                                            </div>
                                        </div>

                                        <div v-if="currentRegion.type === 'checkbox' && currentRegion.bbox" class="form-group">
                                            <label>Checkbox Region:</label>
                                            <div class="checkbox-region-controls">
                                                <button
                                                        @click="toggleCheckboxRegionEditing"
                                                        :class="['btn-checkbox-region', isEditingCheckboxRegion ? 'btn-secondary' : 'btn-primary']"
                                                        type="button">
                                                    {{ isEditingCheckboxRegion ? 'Checkbox Region beenden' : 'Checkbox Region definieren' }}
                                                </button>
                                                <div v-if="currentRegion.checkboxBox" class="checkbox-status">
                                                    <span class="status-success">✓ Checkbox Region definiert</span>
                                                </div>
                                                <div v-else class="checkbox-status">
                                                    <span class="status-warning">⚠ Checkbox Region auswählen</span>
                                                </div>
                                            </div>
                                            <div v-if="isEditingCheckboxRegion" class="checkbox-hint">
                                                <p><strong>Anleitung:</strong> Wählen Sie den exakten Bereich der Checkbox innerhalb des Begrenzungsrahmens aus.</p>
                                            </div>
                                        </div>

                                        <div class="form-group">
                                            <label>Status:</label>
                                            <div class="status-display">
                                                <span v-if="currentRegion.bbox" class="status-success">
                                                    ✓ Begrenzungsrahmen ausgewählt
                                                </span>
                                                <span v-else class="status-warning">
                                                    ⚠ Begrenzungsrahmen auswählen
                                                </span>
                                            </div>
                                        </div>

                                        <div v-if="!isEditingRegion" class="form-group">
                                            <div class="selection-hint">
                                                <p><strong>Anleitung:</strong> Wählen Sie einen Bereich auf dem Template aus, um den Begrenzungsrahmen zu definieren.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="form-actions">
                                        <button @click="closeRegionForm" class="btn-secondary btn-sm">Abbrechen</button>
                                        <button @click="saveRegion" :disabled="!canSaveRegion" class="btn-primary btn-sm">
                                            Speichern
                                        </button>
                                    </div>
                                </div>

                                <div v-else class="region-hint">
                                    <p>Wählen Sie eine Region aus, um sie zu bearbeiten, oder klicken Sie auf "neue Region hinzufügen"</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button @click="showDeleteModal = true" class="btn-danger">
                        <i class="icon-trash"></i>
                        Template löschen
                    </button>
                    <div class="footer-actions">
                        <button @click="closeEditModal" class="btn-secondary">Abbrechen</button>
                        <button @click="saveRegions" :disabled="saving" class="btn-primary">
                            {{ saving ? 'Wird gespeichert...' : 'Speichern' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
            <div class="modal" @click.stop>
                <div class="modal-header">
                    <h2>Template löschen</h2>
                    <button @click="showDeleteModal = false" class="btn-close">×</button>
                </div>
                <div class="modal-body">
                    <p>Möchten Sie das Template "{{ selectedTemplateName }}" wirklich löschen?</p>
                    <p class="warning-text">Diese Aktion kann nicht rückgängig gemacht werden.</p>
                </div>
                <div class="modal-footer">
                    <button @click="showDeleteModal = false" class="btn-secondary">Abbrechen</button>
                    <button @click="deleteTemplate" :disabled="deleting" class="btn-danger">
                        {{ deleting ? 'Wird gelöscht...' : 'Template löschen' }}
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
export default {
    name: 'Clarli',
    data() {
        return {
            templates: [],
            templateImages: {}, // Store authenticated image URLs
            showUploadModal: false,
            showEditModal: false,
            showDeleteModal: false,
            showRegionForm: false,
            selectedFile: null,
            uploading: false,
            saving: false,
            deleting: false,
            loadingTemplates: false,
            loadingRegions: false,
            loadingImage: false,
            selectedTemplateName: '',
            regions: [],
            selectedRegionIndex: -1,
            isEditingRegion: false,
            editingRegionIndex: -1,
            currentRegion: {
                name: '',
                type: 'checkbox',
                bbox: null,
                checkboxBox: null,
                includeTemplateTextAbove: false,
                includeTemplateTextBelow: false,
                includeTemplateTextLeft: false,
                includeTemplateTextRight: false,
                text: ''
            },
            isDrawing: false,
            drawingStart: { x: 0, y: 0 },
            drawingEnd: { x: 0, y: 0 },
            canvasContext: null,
            canvasImage: null,
            canvasScale: { x: 1, y: 1 },
            canvasOffset: { x: 0, y: 0 },
            zoomLevel: 1,
            zoomOffset: { x: 0, y: 0 },
            isPanning: false,
            lastPanPoint: { x: 0, y: 0 },
            isEditingCheckboxRegion: false
        }
    },
    computed: {
        canSaveRegion() {
            return this.currentRegion.name.trim() &&
                (this.isEditingRegion || this.currentRegion.bbox)
        }
    },
    async mounted() {
        await this.loadTemplates()
    },
    beforeDestroy() {
        // Clean up object URLs to prevent memory leaks
        Object.values(this.templateImages).forEach(url => {
            if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url)
            }
        })
    },
    methods: {
        async loadTemplates() {
            this.loadingTemplates = true
            try {
                const response = await this.$http.get('/order/api/Templates/all')
                if (response.data.success) {
                    this.templates = response.data.result.templates
                }
            } catch (error) {
                console.error('Fehler beim Laden der Templates:', error)
                this.showToast('Fehler beim Laden der Templates', 'error')
            } finally {
                this.loadingTemplates = false
            }
        },

        async loadTemplateImage(templateName) {
            this.loadingImage = true
            try {
                const response = await this.$http.get(`/order/api/Templates/${templateName}/visualization`, {
                    responseType: 'blob'
                })

                // Convert blob to data URL
                const blob = new Blob([response.data], { type: 'image/png' })
                const dataUrl = URL.createObjectURL(blob)
                this.$set(this.templateImages, templateName, dataUrl)
            } catch (error) {
                console.error(`Fehler beim Laden des Template-Bildes ${templateName}:`, error)
                // Set a placeholder or error image
                this.$set(this.templateImages, templateName, '')
            } finally {
                this.loadingImage = false
            }
        },

        async editTemplate(templateName) {
            this.selectedTemplateName = templateName
            this.showEditModal = true

            // Load regions and image in parallel
            await Promise.allSettled([
                this.loadRegions(templateName),
                this.loadTemplateImage(templateName)
            ])

            // Initialize canvas after modal is shown
            this.$nextTick(() => {
                this.initializeCanvas()
            })
        },

        async loadRegions(templateName) {
            this.loadingRegions = true
            try {
                const response = await this.$http.get(`/order/api/Templates/${templateName}/regions`)
                if (response.data.success) {
                    this.regions = response.data.result
                }
            } catch (error) {
                console.error('Fehler beim Laden der Regionen:', error)
                this.regions = []
                this.showToast('Fehler beim Laden der Regionen', 'error')
            } finally {
                this.loadingRegions = false
            }
        },

        handleFileSelect(event) {
            const file = event.target.files[0]
            if (file && file.type === 'application/pdf') {
                this.selectedFile = file
            } else {
                this.showToast('Bitte wählen Sie eine PDF-Datei aus', 'error')
            }
        },

        handleFileDrop(event) {
            const file = event.dataTransfer.files[0]
            if (file && file.type === 'application/pdf') {
                this.selectedFile = file
            } else {
                this.showToast('Bitte ziehen Sie eine PDF-Datei hinein', 'error')
            }
        },

        async uploadTemplate() {
            if (!this.selectedFile) return

            this.uploading = true
            try {
                const formData = new FormData()
                formData.append('postedFile', this.selectedFile)

                const response = await this.$http.post('/order/api/Templates/create', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })

                if (response.data.success) {
                    this.showUploadModal = false
                    this.selectedFile = null
                    await this.loadTemplates()
                    this.showToast('Template erfolgreich hochgeladen', 'success')
                }
            } catch (error) {
                console.error('Fehler beim Hochladen:', error)
                this.showToast('Fehler beim Hochladen des Templates', 'error')
            } finally {
                this.uploading = false
            }
        },

        closeEditModal() {
            this.showEditModal = false
            this.selectedTemplateName = ''
            this.regions = []
            this.selectedRegionIndex = -1
            this.zoomLevel = 1
            this.zoomOffset = { x: 0, y: 0 }
            this.closeRegionForm()
        },

        async deleteTemplate() {
            this.deleting = true
            try {
                const response = await this.$http.delete(`/order/api/Templates/${this.selectedTemplateName}`)
                if (response.data.success) {
                    this.showToast('Template erfolgreich gelöscht', 'success')
                    this.showDeleteModal = false
                    this.closeEditModal()
                    await this.loadTemplates()
                }
            } catch (error) {
                console.error('Fehler beim Löschen des Templates:', error)
                this.showToast('Fehler beim Löschen des Templates', 'error')
            } finally {
                this.deleting = false
            }
        },

        selectRegion(index) {
            this.selectedRegionIndex = index
            this.highlightRegion(index)
            // Automatically show edit form when region is selected
            this.editRegion(index)
        },

        startNewRegion() {
            this.isEditingRegion = false
            this.editingRegionIndex = -1
            this.resetCurrentRegion()
            this.currentRegion.name = this.generateDefaultRegionName()
            this.showRegionForm = true
            this.selectedRegionIndex = -1
        },

        editRegion(index) {
            this.isEditingRegion = true
            this.editingRegionIndex = index
            this.currentRegion = { ...this.regions[index] }
            this.showRegionForm = true
            // Ensure checkboxBox is properly copied
            if (this.regions[index].checkboxBox) {
                this.currentRegion.checkboxBox = [...this.regions[index].checkboxBox]
            } else if (this.currentRegion.type === 'checkbox' && this.currentRegion.bbox) {
                // Create default checkboxBox if it doesn't exist
                const [x1, y1, x2, y2] = this.currentRegion.bbox
                this.createDefaultCheckboxBox(x1, y1, x2, y2)
                // Also update the regions array immediately
                this.regions[index].checkboxBox = [...this.currentRegion.checkboxBox]
                // Redraw canvas to show the new checkbox box
                this.drawCanvas()
            }
        },

        closeRegionForm() {
            this.showRegionForm = false
            this.isEditingRegion = false
            this.editingRegionIndex = -1
            this.selectedRegionIndex = -1
            this.resetCurrentRegion()
            this.drawCanvas()
        },

        resetCurrentRegion() {
            this.currentRegion = {
                name: '',
                type: 'checkbox',
                bbox: null,
                checkboxBox: null,
                includeTemplateTextAbove: false,
                includeTemplateTextBelow: false,
                includeTemplateTextLeft: false,
                includeTemplateTextRight: false,
                text: ''
            }
            this.isEditingCheckboxRegion = false
        },

        generateDefaultRegionName() {
            const existingNames = this.regions.map(region => region.name.toLowerCase())
            let counter = 1
            let defaultName

            do {
                defaultName = `Region${counter.toString().padStart(2, '0')}`
                counter++
            } while (existingNames.includes(defaultName.toLowerCase()))

            return defaultName
        },

        toggleCheckboxRegionEditing() {
            this.isEditingCheckboxRegion = !this.isEditingCheckboxRegion
            if (!this.isEditingCheckboxRegion) {
                // Clear any temporary checkbox region when exiting edit mode
                this.drawingStart = { x: 0, y: 0 }
                this.drawingEnd = { x: 0, y: 0 }
            }
            this.drawCanvas()
        },

        createDefaultCheckboxBox(x1, y1, x2, y2) {
            // Create a square checkbox box at the beginning of the ROI with a small margin
            const margin = 5 // 5 pixel margin from the left border
            const checkboxSize = Math.min(x2 - x1 - margin * 2, y2 - y1 - margin * 2) // Square size

            // Ensure minimum size
            const minSize = 10
            const finalSize = Math.max(checkboxSize, minSize)

            const checkboxX1 = x1 + margin
            const checkboxY1 = y1 + margin
            const checkboxX2 = checkboxX1 + finalSize
            const checkboxY2 = checkboxY1 + finalSize

            // Ensure coordinates are within bounds
            const finalX1 = Math.max(checkboxX1, x1)
            const finalY1 = Math.max(checkboxY1, y1)
            const finalX2 = Math.min(checkboxX2, x2)
            const finalY2 = Math.min(checkboxY2, y2)

            this.currentRegion.checkboxBox = [finalX1, finalY1, finalX2, finalY2]
        },

        saveRegion() {
            if (!this.canSaveRegion) return

            const regionData = {
                name: this.currentRegion.name.trim(),
                type: this.currentRegion.type,
                bbox: this.currentRegion.bbox || [0, 0, 100, 100],
                includeTemplateTextAbove: this.currentRegion.includeTemplateTextAbove,
                includeTemplateTextBelow: this.currentRegion.includeTemplateTextBelow,
                includeTemplateTextLeft: this.currentRegion.includeTemplateTextLeft,
                includeTemplateTextRight: this.currentRegion.includeTemplateTextRight
            }

            // Add checkbox-specific properties if type is checkbox
            if (this.currentRegion.type === 'checkbox') {
                regionData.text = this.currentRegion.text || ''
                regionData.checkboxBox = this.currentRegion.checkboxBox || null
            }

            if (this.isEditingRegion) {
                // Update existing region
                this.$set(this.regions, this.editingRegionIndex, regionData)
                this.selectedRegionIndex = this.editingRegionIndex
            } else {
                // Add new region
                this.regions.push(regionData)
                this.selectedRegionIndex = this.regions.length - 1
            }

            this.closeRegionForm()
            this.drawCanvas()
        },

        deleteRegion(index) {
            if (confirm('Region wirklich löschen?')) {
                this.regions.splice(index, 1)
                if (this.selectedRegionIndex === index) {
                    this.selectedRegionIndex = -1
                } else if (this.selectedRegionIndex > index) {
                    this.selectedRegionIndex--
                }
                this.drawCanvas()
            }
        },

        async saveRegions() {
            this.saving = true
            try {
                // Ensure all bbox and checkboxBox coordinates are integers
                const regionsToSave = this.regions.map(region => {
                    const regionData = {
                        ...region,
                        bbox: region.bbox.map(coord => Math.round(coord))
                    }

                    // Also round checkboxBox coordinates if they exist
                    if (region.checkboxBox) {
                        regionData.checkboxBox = region.checkboxBox.map(coord => Math.round(coord))
                    }
                    return regionData
                })

                const response = await this.$http.put(`/order/api/Templates/${this.selectedTemplateName}/regions`, regionsToSave)
                if (response.data.success) {
                    // Clear the cached image to force a fresh fetch
                    this.$set(this.templateImages, this.selectedTemplateName, '')

                    this.showToast('Regionen erfolgreich gespeichert', 'success')
                    this.closeEditModal()
                }
            } catch (error) {
                console.error('Fehler beim Speichern der Regionen:', error)
                this.showToast('Fehler beim Speichern der Regionen', 'error')
            } finally {
                this.saving = false
            }
        },

        initializeCanvas() {
            const canvas = this.$refs.mainCanvas
            if (!canvas) return

            const container = canvas.parentElement
            const containerRect = container.getBoundingClientRect()

            // Set canvas size to container size with proper device pixel ratio
            const devicePixelRatio = window.devicePixelRatio || 1
            canvas.width = containerRect.width * devicePixelRatio
            canvas.height = containerRect.height * devicePixelRatio

            // Scale the canvas back down using CSS
            canvas.style.width = containerRect.width + 'px'
            canvas.style.height = containerRect.height + 'px'

            // Scale the drawing context so everything draws at the correct size
            this.canvasContext = canvas.getContext('2d')
            this.canvasContext.scale(devicePixelRatio, devicePixelRatio)

            // Load and draw the template image
            this.loadAndDrawTemplate()
        },

        async loadAndDrawTemplate() {
            if (!this.canvasContext || !this.templateImages[this.selectedTemplateName]) return

            const canvas = this.$refs.mainCanvas
            const img = new Image()

            img.onload = () => {
                // Get the actual display size of the canvas (CSS size)
                const displayWidth = canvas.style.width ? parseInt(canvas.style.width) : canvas.width
                const displayHeight = canvas.style.height ? parseInt(canvas.style.height) : canvas.height

                // Calculate scaling to fit image in canvas while maintaining aspect ratio
                const canvasAspect = displayWidth / displayHeight
                const imageAspect = img.width / img.height

                let drawWidth, drawHeight

                if (imageAspect > canvasAspect) {
                    // Image is wider than canvas
                    drawWidth = displayWidth
                    drawHeight = displayWidth / imageAspect
                    this.canvasOffset.y = (displayHeight - drawHeight) / 2
                    this.canvasOffset.x = 0
                } else {
                    // Image is taller than canvas
                    drawHeight = displayHeight
                    drawWidth = displayHeight * imageAspect
                    this.canvasOffset.x = (displayWidth - drawWidth) / 2
                    this.canvasOffset.y = 0
                }

                this.canvasScale.x = drawWidth / img.width
                this.canvasScale.y = drawHeight / img.height

                // Store the image for redrawing
                this.canvasImage = img

                this.drawCanvas()
            }

            img.src = this.templateImages[this.selectedTemplateName]
        },

        drawCanvas() {
            if (!this.canvasContext || !this.canvasImage) return

            const canvas = this.$refs.mainCanvas
            const displayWidth = canvas.style.width ? parseInt(canvas.style.width) : canvas.width
            const displayHeight = canvas.style.height ? parseInt(canvas.style.height) : canvas.height

            // Clear canvas and fill with light gray background
            this.canvasContext.fillStyle = '#f5f5f5'
            this.canvasContext.fillRect(0, 0, displayWidth, displayHeight)

            // Apply zoom transformation
            this.canvasContext.save()
            this.canvasContext.translate(this.zoomOffset.x, this.zoomOffset.y)
            this.canvasContext.scale(this.zoomLevel, this.zoomLevel)

            // Draw template image
            this.canvasContext.drawImage(
                this.canvasImage,
                this.canvasOffset.x,
                this.canvasOffset.y,
                this.canvasImage.width * this.canvasScale.x,
                this.canvasImage.height * this.canvasScale.y
            )

            // Draw regions (now using original canvas coordinates since we're in transformed context)
            this.drawRegionsInTransformedContext()

            // Draw temporary purple box for unsaved region
            this.drawTemporaryBoxInTransformedContext()

            // Restore transformation
            this.canvasContext.restore()

            // Draw current selection (outside transformed context)
            this.drawCurrentSelection()

            // Draw hint if no region is selected (not affected by zoom)
            if (this.selectedRegionIndex === -1 && !this.showRegionForm) {
                this.drawHint()
            }
        },

        drawRegions() {
            if (!this.canvasContext) return

            this.regions.forEach((region, index) => {
                const [x1, y1, x2, y2] = region.bbox

                // Convert image coordinates to screen coordinates
                const screenPoint1 = this.imageToScreenCoordinates(x1, y1)
                const screenPoint2 = this.imageToScreenCoordinates(x2, y2)

                // Calculate width and height for drawing
                const canvasWidth = screenPoint2.x - screenPoint1.x
                const canvasHeight = screenPoint2.y - screenPoint1.y

                // Draw region rectangle
                this.canvasContext.strokeStyle = index === this.selectedRegionIndex ? '#ff0000' : '#00ff00'
                this.canvasContext.lineWidth = 2
                this.canvasContext.strokeRect(screenPoint1.x, screenPoint1.y, canvasWidth, canvasHeight)

                // Draw region name
                this.canvasContext.fillStyle = '#000000'
                this.canvasContext.font = '12px Arial'
                this.canvasContext.fillText(region.name, screenPoint1.x, screenPoint1.y - 5)
            })
        },

        drawRegionsInTransformedContext() {
            if (!this.canvasContext) return

            this.regions.forEach((region, index) => {
                const [x1, y1, x2, y2] = region.bbox

                // Convert image coordinates to canvas coordinates (no zoom transformation needed since we're in transformed context)
                const canvasX1 = x1 * this.canvasScale.x + this.canvasOffset.x
                const canvasY1 = y1 * this.canvasScale.y + this.canvasOffset.y
                const canvasX2 = x2 * this.canvasScale.x + this.canvasOffset.x
                const canvasY2 = y2 * this.canvasScale.y + this.canvasOffset.y

                // Calculate width and height for drawing
                const canvasWidth = canvasX2 - canvasX1
                const canvasHeight = canvasY2 - canvasY1

                // Draw region rectangle
                this.canvasContext.strokeStyle = index === this.selectedRegionIndex ? '#ff0000' : '#00ff00'
                this.canvasContext.lineWidth = 2
                this.canvasContext.strokeRect(canvasX1, canvasY1, canvasWidth, canvasHeight)

                // Draw checkbox region if it exists (but not if we're currently editing this region's checkbox)
                if (region.checkboxBox && region.checkboxBox.length === 4 &&
                    !(this.isEditingCheckboxRegion && index === this.selectedRegionIndex)) {
                    const [cbx1, cby1, cbx2, cby2] = region.checkboxBox

                    // Ensure coordinates are valid numbers
                    if (typeof cbx1 === 'number' && typeof cby1 === 'number' &&
                        typeof cbx2 === 'number' && typeof cby2 === 'number' &&
                        cbx1 < cbx2 && cby1 < cby2) {
                        const checkboxCanvasX1 = cbx1 * this.canvasScale.x + this.canvasOffset.x
                        const checkboxCanvasY1 = cby1 * this.canvasScale.y + this.canvasOffset.y
                        const checkboxCanvasX2 = cbx2 * this.canvasScale.x + this.canvasOffset.x
                        const checkboxCanvasY2 = cby2 * this.canvasScale.y + this.canvasOffset.y
                        const checkboxCanvasWidth = checkboxCanvasX2 - checkboxCanvasX1
                        const checkboxCanvasHeight = checkboxCanvasY2 - checkboxCanvasY1

                        // Draw blue checkbox region
                        this.canvasContext.strokeStyle = '#0066CC'
                        this.canvasContext.lineWidth = 2
                        this.canvasContext.setLineDash([])
                        this.canvasContext.strokeRect(checkboxCanvasX1, checkboxCanvasY1, checkboxCanvasWidth, checkboxCanvasHeight)

                        // Draw checkbox label
                        this.canvasContext.fillStyle = '#0066CC'
                        this.canvasContext.font = '10px Arial'
                        this.canvasContext.fillText('Checkbox', checkboxCanvasX1, checkboxCanvasY1 - 5)
                    }
                }

                // Draw region name
                this.canvasContext.fillStyle = '#000000'
                this.canvasContext.font = '12px Arial'
                this.canvasContext.fillText(region.name, canvasX1, canvasY1 - 5)
            })
        },

        highlightRegion(index) {
            this.drawCanvas()
        },

        startDrawing(event) {
            // If no region is selected and no form is shown, start creating a new region
            if (this.selectedRegionIndex === -1 && !this.showRegionForm) {
                this.startNewRegion()
            }

            const canvas = this.$refs.mainCanvas
            const rect = canvas.getBoundingClientRect()
            const mouseX = event.clientX - rect.left
            const mouseY = event.clientY - rect.top

            // Convert screen coordinates to image coordinates
            const imagePoint = this.screenToImageCoordinates(mouseX, mouseY)

            // Check if click is within the image bounds
            if (imagePoint.x < 0 || imagePoint.y < 0 ||
                imagePoint.x > this.canvasImage.width ||
                imagePoint.y > this.canvasImage.height) {
                return
            }

            // If editing checkbox region, check if click is within bbox boundaries
            if (this.isEditingCheckboxRegion && this.currentRegion.bbox) {
                const [x1, y1, x2, y2] = this.currentRegion.bbox
                if (imagePoint.x < x1 || imagePoint.x > x2 ||
                    imagePoint.y < y1 || imagePoint.y > y2) {
                    return // Click is outside bbox boundaries
                }
            }

            this.isDrawing = true
            this.drawingStart = { x: imagePoint.x, y: imagePoint.y }
            this.drawingEnd = { x: imagePoint.x, y: imagePoint.y }
        },

        draw(event) {
            if (!this.isDrawing) return

            const canvas = this.$refs.mainCanvas
            const rect = canvas.getBoundingClientRect()
            const mouseX = event.clientX - rect.left
            const mouseY = event.clientY - rect.top

            // Convert screen coordinates to image coordinates
            const imagePoint = this.screenToImageCoordinates(mouseX, mouseY)

            // Clamp coordinates to image bounds
            let clampedX = Math.max(0, Math.min(imagePoint.x, this.canvasImage.width))
            let clampedY = Math.max(0, Math.min(imagePoint.y, this.canvasImage.height))

            // If editing checkbox region, clamp coordinates to bbox boundaries
            if (this.isEditingCheckboxRegion && this.currentRegion.bbox) {
                const [x1, y1, x2, y2] = this.currentRegion.bbox
                clampedX = Math.max(x1, Math.min(clampedX, x2))
                clampedY = Math.max(y1, Math.min(clampedY, y2))
            }

            this.drawingEnd = { x: clampedX, y: clampedY }
            this.drawCanvas()
            this.drawCurrentSelection()
        },

        stopDrawing() {
            if (!this.isDrawing) return

            this.isDrawing = false

            // Calculate bounding box
            const x1 = Math.round(Math.min(this.drawingStart.x, this.drawingEnd.x))
            const y1 = Math.round(Math.min(this.drawingStart.y, this.drawingEnd.y))
            const x2 = Math.round(Math.max(this.drawingStart.x, this.drawingEnd.x))
            const y2 = Math.round(Math.max(this.drawingStart.y, this.drawingEnd.y))

            if (this.isEditingCheckboxRegion) {
                // Set checkbox region
                this.currentRegion.checkboxBox = [x1, y1, x2, y2]
            } else if (this.showRegionForm && !this.isEditingRegion) {
                // Set bounding box for new region
                this.currentRegion.bbox = [x1, y1, x2, y2]

                // Auto-create checkboxBox for checkbox type regions
                if (this.currentRegion.type === 'checkbox') {
                    this.createDefaultCheckboxBox(x1, y1, x2, y2)
                }
            } else if (this.selectedRegionIndex >= 0) {
                // Update existing region - update both the regions array and currentRegion
                this.regions[this.selectedRegionIndex].bbox = [x1, y1, x2, y2]
                this.currentRegion.bbox = [x1, y1, x2, y2]
                // Note: Don't auto-create checkboxBox when editing existing regions
            }

            this.drawCanvas()
        },

        drawTemporaryBox() {
            if (!this.canvasContext || !this.currentRegion.bbox || this.isEditingRegion) return

            const [x1, y1, x2, y2] = this.currentRegion.bbox

            // Convert image coordinates to screen coordinates
            const screenPoint1 = this.imageToScreenCoordinates(x1, y1)
            const screenPoint2 = this.imageToScreenCoordinates(x2, y2)

            // Calculate width and height for drawing
            const canvasWidth = screenPoint2.x - screenPoint1.x
            const canvasHeight = screenPoint2.y - screenPoint1.y

            // Draw purple temporary box
            this.canvasContext.strokeStyle = '#8B5CF6'
            this.canvasContext.lineWidth = 2
            this.canvasContext.setLineDash([8, 4])
            this.canvasContext.strokeRect(screenPoint1.x, screenPoint1.y, canvasWidth, canvasHeight)
            this.canvasContext.setLineDash([])
        },

        drawTemporaryBoxInTransformedContext() {
            if (!this.canvasContext || !this.currentRegion.bbox || this.isEditingRegion) return

            const [x1, y1, x2, y2] = this.currentRegion.bbox

            // Convert image coordinates to canvas coordinates (no zoom transformation needed since we're in transformed context)
            const canvasX1 = x1 * this.canvasScale.x + this.canvasOffset.x
            const canvasY1 = y1 * this.canvasScale.y + this.canvasOffset.y
            const canvasX2 = x2 * this.canvasScale.x + this.canvasOffset.x
            const canvasY2 = y2 * this.canvasScale.y + this.canvasOffset.y

            // Calculate width and height for drawing
            const canvasWidth = canvasX2 - canvasX1
            const canvasHeight = canvasY2 - canvasY1

            // Draw purple temporary box
            this.canvasContext.strokeStyle = '#8B5CF6'
            this.canvasContext.lineWidth = 2
            this.canvasContext.setLineDash([8, 4])
            this.canvasContext.strokeRect(canvasX1, canvasY1, canvasWidth, canvasHeight)
            this.canvasContext.setLineDash([])
        },

        drawCurrentSelection() {
            if (!this.canvasContext || !this.isDrawing) return

            const x1 = Math.min(this.drawingStart.x, this.drawingEnd.x)
            const y1 = Math.min(this.drawingStart.y, this.drawingEnd.y)
            const x2 = Math.max(this.drawingStart.x, this.drawingEnd.x)
            const y2 = Math.max(this.drawingStart.y, this.drawingEnd.y)

            // Convert image coordinates to screen coordinates
            const screenPoint1 = this.imageToScreenCoordinates(x1, y1)
            const screenPoint2 = this.imageToScreenCoordinates(x2, y2)

            // Calculate width and height for drawing
            const canvasWidth = screenPoint2.x - screenPoint1.x
            const canvasHeight = screenPoint2.y - screenPoint1.y

            // Use different colors for checkbox region editing
            if (this.isEditingCheckboxRegion) {
                this.canvasContext.strokeStyle = '#0066CC'
            } else {
                this.canvasContext.strokeStyle = '#ff0000'
            }
            this.canvasContext.lineWidth = 2
            this.canvasContext.setLineDash([5, 5])
            this.canvasContext.strokeRect(screenPoint1.x, screenPoint1.y, canvasWidth, canvasHeight)
            this.canvasContext.setLineDash([])
        },

        drawHint() {
            if (!this.canvasContext) return

            const canvas = this.$refs.mainCanvas
            const displayWidth = canvas.style.width ? parseInt(canvas.style.width) : canvas.width
            const displayHeight = canvas.style.height ? parseInt(canvas.style.height) : canvas.height
            const centerX = displayWidth / 2
            const centerY = displayHeight / 2

            // Draw hint background
            this.canvasContext.fillStyle = 'rgba(0, 0, 0, 0.7)'
            this.canvasContext.fillRect(centerX - 200, centerY - 30, 400, 60)

            // Draw hint text
            this.canvasContext.fillStyle = '#ffffff'
            this.canvasContext.font = '16px Arial'
            this.canvasContext.textAlign = 'center'
            this.canvasContext.fillText('Wählen Sie eine Region aus, um sie zu bearbeiten', centerX, centerY + 5)
            this.canvasContext.textAlign = 'left'
        },

        showToast(message, type = 'info') {
            // Simple toast implementation - you can replace this with your preferred toast library
            const toast = document.createElement('div')
            toast.className = `toast toast-${type}`
            toast.textContent = message
            document.body.appendChild(toast)

            // Remove toast after 3 seconds
            setTimeout(() => {
                if (toast.parentNode) {
                    toast.parentNode.removeChild(toast)
                }
            }, 3000)
        },

        handleImageError(event) {
            event.target.style.display = 'none'
        },

        // Zoom functionality
        zoomIn() {
            if (this.zoomLevel < 4) {
                const newZoom = Math.min(4, this.zoomLevel * 1.25)
                this.zoomToCenter(newZoom)
            }
        },

        zoomOut() {
            if (this.zoomLevel > 0.25) {
                const newZoom = Math.max(0.25, this.zoomLevel / 1.25)
                this.zoomToCenter(newZoom)
            }
        },

        resetZoom() {
            this.zoomLevel = 1
            this.zoomOffset = { x: 0, y: 0 }
            this.drawCanvas()
        },

        zoomToCenter(newZoom) {
            const canvas = this.$refs.mainCanvas
            if (!canvas) return

            const displayWidth = canvas.style.width ? parseInt(canvas.style.width) : canvas.width
            const displayHeight = canvas.style.height ? parseInt(canvas.style.height) : canvas.height
            const centerX = displayWidth / 2
            const centerY = displayHeight / 2

            // Get the current point at the center in image coordinates
            const imagePoint = this.screenToImageCoordinates(centerX, centerY)

            // Update zoom level
            this.zoomLevel = newZoom

            // Calculate new offset to keep the same point at center
            const newScreenPoint = this.imageToScreenCoordinates(imagePoint.x, imagePoint.y)
            this.zoomOffset.x = centerX - newScreenPoint.x
            this.zoomOffset.y = centerY - newScreenPoint.y

            this.drawCanvas()
        },

        handleWheel(event) {
            event.preventDefault()

            const canvas = this.$refs.mainCanvas
            if (!canvas) return

            const rect = canvas.getBoundingClientRect()
            const mouseX = event.clientX - rect.left
            const mouseY = event.clientY - rect.top

            // Calculate zoom factor
            const delta = event.deltaY > 0 ? 0.9 : 1.1
            const newZoom = Math.max(0.25, Math.min(4, this.zoomLevel * delta))

            if (newZoom === this.zoomLevel) return

            // Get the current point under the mouse in image coordinates
            const imagePoint = this.screenToImageCoordinates(mouseX, mouseY)

            // Update zoom level
            this.zoomLevel = newZoom

            // Calculate new offset to keep the same image point under the mouse
            const newScreenPoint = this.imageToScreenCoordinates(imagePoint.x, imagePoint.y)
            this.zoomOffset.x += mouseX - newScreenPoint.x
            this.zoomOffset.y += mouseY - newScreenPoint.y

            this.drawCanvas()
        },

        // Convert screen coordinates to image coordinates
        screenToImageCoordinates(screenX, screenY) {
            // Remove zoom offset and zoom level
            const unzoomedX = (screenX - this.zoomOffset.x) / this.zoomLevel
            const unzoomedY = (screenY - this.zoomOffset.y) / this.zoomLevel

            // Convert to image coordinates
            const imageX = (unzoomedX - this.canvasOffset.x) / this.canvasScale.x
            const imageY = (unzoomedY - this.canvasOffset.y) / this.canvasScale.y

            return { x: imageX, y: imageY }
        },

        // Convert image coordinates to screen coordinates
        imageToScreenCoordinates(imageX, imageY) {
            // Convert to canvas coordinates
            const canvasX = imageX * this.canvasScale.x + this.canvasOffset.x
            const canvasY = imageY * this.canvasScale.y + this.canvasOffset.y

            // Apply zoom and offset
            const screenX = canvasX * this.zoomLevel + this.zoomOffset.x
            const screenY = canvasY * this.zoomLevel + this.zoomOffset.y

            return { x: screenX, y: screenY }
        }
    }
}
</script>

<style lang="scss" scoped>
.clarli-container {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;

    h1 {
        margin: 0;
        color: #333;
    }
}

.btn-primary {
    background: #007bff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: #0056b3;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
}

.btn-secondary {
    background: #6c757d;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background: #545b62;
    }
}

.btn-danger {
    background: #dc3545;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;

    &:hover {
        background: #c82333;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
}

.btn-sm {
    padding: 6px 12px;
    font-size: 12px;
}

.template-list {

    .empty-state,
    .loading-state {
        text-align: center;
        padding: 40px;
        color: #666;
    }

    .loading-state {
        .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
        }
    }

    .template-table {
        background: white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        table {
            width: 100%;
            border-collapse: collapse;

            th,
            td {
                padding: 12px 16px;
                text-align: left;
                border-bottom: 1px solid #eee;
            }

            th {
                background: #f8f9fa;
                font-weight: 600;
                color: #333;
            }

            tr:hover {
                background: #f8f9fa;
            }

            tr:last-child td {
                border-bottom: none;
            }
        }
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    &:has(.modal.large) {
        align-items: stretch;
        justify-content: stretch;
    }
}

.modal {
    background: white;
    border-radius: 8px;
    width: 90%;
    height: auto;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &.large {
        max-width: none;
        width: 100%;
        height: 100%;
        max-height: none;
        border-radius: 0;
    }

    .modal-header {
        padding: 20px;
        border-bottom: 1px solid #ddd;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h2 {
            margin: 0;
            color: #333;
        }
    }

    .modal-body {
        padding: 20px;
        overflow-y: auto;
        flex: 1;
        display: flex;
        flex-direction: column;

        .warning-text {
            color: #dc3545;
            font-weight: 500;
            margin-top: 10px;
        }
    }

    .modal-footer {
        padding: 20px;
        border-top: 1px solid #ddd;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;

        .footer-actions {
            display: flex;
            gap: 10px;
        }
    }
}

.btn-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;

    &:hover {
        color: #333;
    }
}

.file-upload {
    .upload-area {
        border: 2px dashed #ddd;
        border-radius: 8px;
        padding: 40px;
        text-align: center;
        cursor: pointer;
        transition: border-color 0.2s;

        &:hover {
            border-color: #007bff;
        }

        .upload-placeholder {
            color: #666;

            i {
                font-size: 48px;
                margin-bottom: 10px;
                display: block;
            }
        }

        .file-selected {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    }
}

.btn-remove {
    background: #dc3545;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background: #c82333;
    }
}

.template-editor {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 20px;
    height: 100%;
    min-height: 600px;
}

.template-visualization {
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .image-loading-state {
        text-align: center;
        padding: 40px;
        color: #666;

        .spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
        }
    }

    .canvas-container {
        position: relative;
        width: 100%;
        height: 100%;
    }

    .zoom-controls {
        position: absolute;
        top: 15px;
        right: 15px;
        display: flex;
        align-items: center;
        gap: 2px;
        background: rgba(255, 255, 255, 0.95);
        padding: 8px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        backdrop-filter: blur(10px);
        z-index: 10;
        border: 1px solid rgba(0, 0, 0, 0.1);

        .btn-zoom {
            background: #ffffff;
            color: #333;
            border: 1px solid #ddd;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            font-size: 14px;

            &:hover:not(:disabled) {
                background: #f8f9fa;
                border-color: #007bff;
                color: #007bff;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
            }

            &:active:not(:disabled) {
                transform: translateY(0);
                box-shadow: 0 1px 4px rgba(0, 123, 255, 0.3);
            }

            &:disabled {
                background: #f8f9fa;
                color: #6c757d;
                border-color: #e9ecef;
                cursor: not-allowed;
                opacity: 0.6;
            }
        }

        .btn-zoom-reset {
            background: #6c757d;
            color: white;
            border: none;
            height: 32px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            font-size: 12px;

            &:hover {
                background: #5a6268;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
            }

            &:active {
                transform: translateY(0);
                box-shadow: 0 1px 4px rgba(108, 117, 125, 0.4);
            }
        }

        .zoom-level-display {
            padding: 0 8px;
            min-width: 50px;
            text-align: center;

            .zoom-percentage {
                font-size: 12px;
                font-weight: 600;
                color: #333;
                background: #f8f9fa;
                padding: 4px 8px;
                border-radius: 4px;
                border: 1px solid #e9ecef;
            }
        }
    }

    .main-canvas {
        width: 100%;
        height: 100%;
        cursor: crosshair;
    }
}

.regions-panel {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;

    .regions-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        flex-shrink: 0;

        h3 {
            margin: 0;
            color: #333;
            font-size: 16px;
        }

        .header-actions {
            display: flex;
            gap: 8px;
        }
    }

    .loading-state {
        text-align: center;
        padding: 20px;
        color: #666;

        .spinner {
            width: 30px;
            height: 30px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 10px;
        }
    }

    .regions-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    .regions-list {
        flex: 1;
        overflow-y: auto;
        margin-bottom: 10px;
        min-height: 0;

        .region-item {
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            margin-bottom: 6px;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;

            &.active {
                border-color: #007bff;
                background: #f8f9ff;
            }

            .region-info {
                flex: 1;

                .region-name {
                    font-weight: bold;
                    display: block;
                    font-size: 14px;
                }

                .region-type {
                    font-size: 11px;
                    color: #666;
                }
            }

            .region-actions {
                display: flex;
                gap: 4px;
            }
        }
    }

    .region-hint {
        text-align: center;
        padding: 20px;
        color: #666;
        font-style: italic;
        border-top: 1px solid #ddd;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .region-form-panel {
        border-top: 1px solid #ddd;
        margin-top: 10px;
        padding-top: 15px;
        flex-shrink: 0;

        .form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;

            h4 {
                margin: 0;
                color: #333;
                font-size: 14px;
                font-weight: 600;
            }

            .btn-close-small {
                background: none;
                border: none;
                font-size: 18px;
                cursor: pointer;
                color: #666;
                padding: 0;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                    color: #333;
                }
            }
        }

        .form-actions {
            display: flex;
            gap: 8px;
            margin-top: 15px;
            padding-top: 15px;
            border-top: 1px solid #eee;
        }
    }
}

.btn-icon {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 5px;

    &:hover {
        background: #f8f9fa;
        border-radius: 4px;
    }

    &.btn-delete {
        color: #dc3545;

        &:hover {
            background: #f8d7da;
            color: #721c24;
        }
    }
}

// Form styles
.region-form {
    .form-group {
        margin-bottom: 15px;

        label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
            color: #333;
            font-size: 13px;
        }

        .form-control {
            width: 100%;
            padding: 6px 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 13px;

            &:focus {
                outline: none;
                border-color: #007bff;
                box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
            }
        }

        .checkbox-group {
            display: flex;
            flex-direction: column;
            gap: 6px;

            .checkbox-label {
                display: flex;
                align-items: center;
                gap: 6px;
                font-weight: normal;
                cursor: pointer;
                font-size: 12px;

                input[type="checkbox"] {
                    margin: 0;
                }
            }
        }

        .status-display {
            padding: 6px 10px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;

            .status-success {
                color: #28a745;
                background: #d4edda;
                border: 1px solid #c3e6cb;
                padding: 4px 8px;
                border-radius: 4px;
                display: inline-block;
            }

            .status-warning {
                color: #856404;
                background: #fff3cd;
                border: 1px solid #ffeaa7;
                padding: 4px 8px;
                border-radius: 4px;
                display: inline-block;
            }
        }

        .selection-hint {
            padding: 10px;
            background: #e3f2fd;
            border: 1px solid #bbdefb;
            border-radius: 4px;
            color: #1976d2;

            p {
                margin: 0;
                font-size: 12px;
            }
        }

        .text-display {
            padding: 8px 12px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 4px;
            font-size: 13px;
            color: #333;
            min-height: 20px;
            word-wrap: break-word;
        }

        .text-hint {
            padding: 8px 12px;
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 4px;
            color: #856404;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 6px;

            .icon-info {
                font-size: 14px;
            }
        }

        .checkbox-region-controls {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .btn-checkbox-region {
                padding: 8px 12px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
                transition: all 0.2s ease;

                &.btn-primary {
                    background: #007bff;
                    color: white;

                    &:hover {
                        background: #0056b3;
                    }
                }

                &.btn-secondary {
                    background: #6c757d;
                    color: white;

                    &:hover {
                        background: #545b62;
                    }
                }
            }

            .checkbox-status {
                margin-top: 4px;
            }
        }

        .checkbox-hint {
            padding: 10px;
            background: #e8f5e8;
            border: 1px solid #c3e6c3;
            border-radius: 4px;
            color: #155724;
            margin-top: 8px;

            p {
                margin: 0;
                font-size: 12px;
            }
        }
    }
}

// Icons
.icon-plus::before {
    content: "+";
}

.icon-upload::before {
    content: "📁";
}

.icon-trash::before {
    content: "🗑";
}

.icon-edit::before {
    content: "✏️";
}

.icon-zoom-in::before {
    content: "+";
}

.icon-zoom-out::before {
    content: "−";
}

.icon-zoom-reset::before {
    content: "⌂";
}

.icon-info::before {
    content: "ℹ";
}

// Spinner animation
@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

// Toast notifications
.toast {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 12px 20px;
    border-radius: 4px;
    color: white;
    font-weight: 500;
    z-index: 10000;
    animation: slideIn 0.3s ease-out;
    max-width: 300px;
    word-wrap: break-word;
}

.toast-success {
    background-color: #28a745;
}

.toast-error {
    background-color: #dc3545;
}

.toast-info {
    background-color: #17a2b8;
}

@keyframes slideIn {
    from {
        transform: translateX(100%);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}
</style>
